/**
 * @typedef { import('focus-trap').FocusTrap } FocusTrap
 * @typedef {HTMLElement & { trap: FocusTrap }} NavPage
 */

if (!customElements.get('header-global')) {
  customElements.define(
    'header-global',
    class GlobalHeader extends HTMLElement {
      constructor() {
        super();

        this.threshold = 200;
      }

      connectedCallback() {
        this.setAttribute('data-scrolled', String(this.scrolled));

        window.addEventListener(
          'scroll',
          () => {
            this.setAttribute('data-scrolled', String(this.scrolled));
          },
          {
            passive: true,
          },
        );
      }

      get scrolled() {
        return window.scrollY > this.threshold;
      }
    },
  );
}

if (!customElements.get('header-tray-menu')) {
  customElements.define(
    'header-tray-menu',
    class HeaderTrayMenu extends HTMLElement {
      constructor() {
        super();

        /** @type {NavPage[]} */
        this.pages = Array.from(this.querySelectorAll('[data-tray-page]'));
      }

      connectedCallback() {
        /** @type {HTMLElement | null} */
        const header = this.closest('header-global');

        const pages = this.pages;

        pages.forEach((page) => {
          /** @type {HTMLElement | null} - Used exclusively for the fallback focus when navigating backwards through the menu tree */
          const close = page.querySelector('[data-tray-close]');

          /** @type { HTMLAnchorElement | HTMLButtonElement | null } */
          const initial = page.querySelector('.section-header-tray-menu-link');

          const scheme = header?.dataset.trayScheme || 'scheme-2';

          window.traps.create(page, {
            onActivate: () => {
              page.setAttribute('aria-expanded', 'true');
              document.body.setAttribute('data-frozen-scroll', 'true');

              if (header) {
                header.setAttribute('data-scheme', scheme);
              }

              const elements = page.querySelectorAll(
                ':scope > div > * > li, :scope > div > ul > animated-details > li, :scope > div > .section-header-tray-image-container .section-header-menu-image-container',
              );

              window.motion.animate(
                elements,
                {
                  opacity: [0, 1],
                },
                {
                  delay: window.motion.stagger(0.1),
                },
              );
            },
            onDeactivate: () => {
              page.setAttribute('aria-expanded', 'false');
            },
            onPostDeactivate: () => {
              if (!window.traps.store['header-tray'].active) {
                document.body.removeAttribute('data-frozen-scroll');

                if (header) {
                  header.removeAttribute('data-scheme');
                }
              }
            },
            initialFocus: initial ?? undefined,
            fallbackFocus: close ?? undefined,
            allowOutsideClick: (e) => {
              const target = e.target;

              if (target && target instanceof Element && target.closest('header a, header button')) {
                return true;
              }

              return false;
            },
          });
        });

        window.addEventListener(
          'resize',
          window.debounce(() => {
            if (window.traps.store['header-tray'].active && window.innerWidth >= 1024) {
              document.body.removeAttribute('data-frozen-scroll');
              window.traps.store['header-tray'].deactivate();

              if (header) {
                header.removeAttribute('data-scheme');
              }
            }
          }, 500),
          {
            passive: true,
          },
        );
      }
    },
  );
}

if (!customElements.get('header-dropdown-menu')) {
  customElements.define(
    'header-dropdown-menu',
    class HeaderTrayMenu extends HTMLElement {
      constructor() {
        super();

        this.opens = this.querySelectorAll('[data-dropdown-open]');
        /** @type {NavPage[]} */
        this.pages = [];
      }

      connectedCallback() {
        this.opens.forEach((open) => {
          const parent = open.parentElement;
          /** @type {NavPage | null | undefined} */
          const page = parent?.querySelector('[data-dropdown-page]');

          open.addEventListener('focus', () => {
            this.pages.forEach((page) => this.closePage(page));
          });

          if (page && parent) {
            this.pages.push(page);
            window.traps.create(page);

            open.addEventListener('focus', () => {
              this.openPage(page);
            });

            /** @type {ReturnType<typeof setTimeout> | null} */
            let timer = null;

            const delay = 250;

            parent.addEventListener('mouseenter', () => {
              if (timer) {
                clearTimeout(timer);
              }

              timer = setTimeout(() => {
                Object.values(window.traps.store).forEach((trap) => trap.deactivate());
                this.openPage(page);

                const elements = page.querySelectorAll(
                  '.section-header-dropdown-menu-item-list-item, .section-header-menu-image',
                );

                window.motion.animate(
                  elements,
                  {
                    opacity: [0, 1],
                  },
                  {
                    delay: window.motion.stagger(0.1),
                  },
                );
              }, delay);
            });

            parent.addEventListener('mouseleave', () => {
              if (timer) {
                clearTimeout(timer);
              }

              timer = setTimeout(() => {
                this.closePage(page);
              }, delay);
            });
          }
        });

        this.addEventListener('keyup', (e) => {
          if (e.key === 'Escape') {
            this.pages.forEach((menu) => this.closePage(menu));
          }
        });

        this.addEventListener('focusout', (e) => {
          if (!this.contains(/** @type {Node} */ (e.relatedTarget))) {
            this.pages.forEach((page) => this.closePage(page));
          }
        });
      }

      /** @param {NavPage} menu - The menu to open */
      openPage(menu) {
        menu.setAttribute('aria-expanded', 'true');
      }

      /** @param {NavPage} menu - The menu to close */
      closePage(menu) {
        menu.setAttribute('aria-expanded', 'false');
      }
    },
  );
}

if (!customElements.get('header-search-menu')) {
  customElements.define(
    'header-search-menu',
    class HeaderSearchMenu extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLButtonElement | null} */
        this.toggle = document.querySelector('[data-focus-trap-toggle="search"]');
      }

      connectedCallback() {
        if (this.toggle) {
          window.traps.create(this, {
            initialFocus: 'input[type="search"]',
            onActivate: () => {
              if (window.innerWidth < 1024) {
                document.body.setAttribute('data-frozen-scroll', 'true');
              }

              this?.setAttribute('aria-expanded', 'true');
            },
            onDeactivate: () => {
              this?.setAttribute('aria-expanded', 'false');
              document.body.removeAttribute('data-frozen-scroll');
            },
            fallbackFocus: this.toggle,
            allowOutsideClick: (e) => {
              const target = e.target;

              if (
                target &&
                target instanceof Element &&
                (target.closest('header-global a, header-global button') || !target.closest('header-global'))
              ) {
                if (!target.closest('header-global')) {
                  window.traps.store['search'].deactivate();
                }

                return true;
              }

              return false;
            },
          });
        }
      }
    },
  );
}

/**
 * @typedef {object} ClerkProductData
 * @property {string} url - The URL of the product
 * @property {string} id - The ID of the product
 */

if (!customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLElement {
      constructor() {
        super();

        /** @type {HTMLFormElement | null} */
        this.form = this.querySelector('form');

        /** @type {HTMLInputElement | null} */
        this.input = this.querySelector('input[type="search"]');

        /** @type {HTMLButtonElement | null} */
        this.clear = this.querySelector('[data-search-clear]');

        /** @type {HTMLElement | null} */
        this.productsList = this.querySelector('[data-products-list]');

        /** @type {HTMLElement | null} */
        this.collectionsList = this.querySelector('[data-collections-list]');

        /** @type {HTMLAnchorElement | null} */
        this.link = this.querySelector('[data-view-all-link]');

        /** @type {HTMLElement | null} */
        this.recent = this.querySelector('[data-recent-list]');

        this.controller = null;
      }

      connectedCallback() {
        if (this.input) {
          this.input.addEventListener(
            'input',
            window.debounce(() => this.changeHandler(), 500),
          );
        }

        if (this.form) {
          this.form.addEventListener('submit', this.submitHandler.bind(this));
        }

        if (this.clear) {
          this.clear.addEventListener('click', this.clearHandler.bind(this));
        }

        this.renderRecentSearches();
      }

      async changeHandler() {
        if (this.controller) {
          this.controller.abort();
        }

        this.controller = new AbortController();
        const signal = this.controller.signal;

        if (!this.input || (!this.productsList && !this.collectionsList)) return;

        const term = this.input.value.trim();

        if (!term.length) {
          this.dataset.loading = 'false';
          return this.clearResults();
        }

        this.dataset.loading = 'true';

        const options = { method: 'GET', headers: { accept: 'application/json' }, signal };

        try {
          const clerkRequest = fetch(
            `https://api.clerk.io/v2/search/predictive?key=nqfwzYFvqB1afrgxh6Dm62gL7yFOnyod&attributes=url,id,tags&query=${encodeURIComponent(
              term,
            )}&filter=tags+contains%20not+combined-listing&limit=5`,
            options,
          );

          const collectionsRequest = fetch(
            `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(
              term,
            )}&resources[type]=collection&resources[limit]=5`,
            options,
          );

          const [clerkResponse, collectionsResponse] = await Promise.all([clerkRequest, collectionsRequest]);

          /** @type {{ html: string, id: string }[]} */
          let items = [];

          if (clerkResponse.ok) {
            const data = await clerkResponse.json();

            const products = (data.product_data || []).map(
              (/** @type {import('./types').ClerkProductData} */ product) => ({
                url: product.url,
                id: String(product.id),
              }),
            );

            items = await this.getProductCards(products);
          }

          const productsList = this.productsList;
          const collectionsList = this.collectionsList;

          this.clearResults();

          if (productsList && productsList instanceof HTMLElement) {
            items.forEach((item) => {
              if (!this.isRenderableProductCard(item.html)) return;

              const li = document.createElement('li');
              li.classList.add('predictive-search-results-item');
              li.dataset.clerkProductId = item.id;
              li.innerHTML = item.html;
              productsList.append(li);
            });

            if (typeof window.Clerk === 'function') {
              window.Clerk('click', '*[data-clerk-product-id]');
            }
          }

          if (collectionsList && collectionsList instanceof HTMLElement) {
            if (collectionsResponse.ok) {
              const collectionsData = await collectionsResponse.json();
              const collections = collectionsData?.resources?.results?.collections || [];

              collections.slice(0, 5).forEach((/** @type {{ title?: string; url?: string }} */ collection) => {
                if (!collection?.url) return;

                const li = document.createElement('li');

                const link = document.createElement('a');
                link.classList.add('predictive-search-collection-link');
                link.href = collection.url;
                link.textContent = collection.title || collection.url;

                li.append(link);
                collectionsList.append(li);
              });
            }
          }

          if (this.link) {
            this.link.href = `${window.Shopify.routes.root}search?q=${encodeURIComponent(term)}`;
          }

          if (this.controller?.signal === signal) {
            this.dataset.loading = 'false';
          }

          if (!clerkResponse.ok && !collectionsResponse.ok) {
            throw new Error(clerkResponse.statusText || collectionsResponse.statusText);
          }
        } catch (/** @type {any} */ error) {
          if (error && error.name === 'AbortError') return;

          if (this.controller?.signal === signal) {
            this.dataset.loading = 'false';
          }

          throw error;
        }
      }

      /**
       * @param {import('./types').ClerkProductData[]} products - The Clerk products to fetch cards for
       * @returns {Promise<{ html: string, id: string }[]>} - The product card strings with Clerk IDs
       */
      async getProductCards(products) {
        if (!products || !Array.isArray(products) || products.length === 0) {
          return [];
        }

        try {
          const requests = products.map(async (product) => {
            try {
              const response = await fetch(
                `${window.Shopify.routes.root}${product.url.slice(1)}?section_id=section-main-product`,
                {
                  method: 'GET',
                },
              );

              if (!response.ok) return null;

              const data = await response.text();

              const html = new DOMParser().parseFromString(data, 'text/html');

              const json = html.querySelector('[data-product-card-compact-json]');

              if (!json || !json.textContent) return null;

              const item = Object.values(JSON.parse(json.textContent))[0];

              if (typeof item !== 'string') return null;

              return { html: item, id: String(product.id) };
            } catch (/** @type {any} */ error) {
              if (error && error.name === 'AbortError') {
                return null;
              }

              throw error;
            }
          });

          const items = await Promise.all(requests);

          return items.filter((item) => item !== null);
        } catch {
          return [];
        }
      }

      /**
       * @param {string} item - Product card markup from section response
       * @returns {boolean} - Whether the card has a real product URL
       */
      isRenderableProductCard(item) {
        if (!item || typeof item !== 'string') return false;

        const html = new DOMParser().parseFromString(item, 'text/html');
        const link = html.querySelector('.product-card-compact-container[data-product-url]');

        if (!link || !(link instanceof HTMLAnchorElement)) return false;

        const href = link.getAttribute('href');

        if (!href) return false;

        return href.trim().length > 0;
      }

      /**
       * @param {Event} e - The form submit event
       */
      async submitHandler(e) {
        e.preventDefault();

        if (this.form) {
          const term = this.input?.value.trim();

          if (term && term.length > 0) {
            const storage = localStorage.getItem('search-terms');
            const store = storage !== null ? JSON.parse(storage) : [];

            if (!store.includes(term)) {
              if (store.length > 5) {
                store.shift();
              }

              store.push(term);
            }

            localStorage.setItem('search-terms', JSON.stringify(store));

            this.form.submit();
          }
        }
      }

      clearHandler() {
        if (this.controller) {
          this.controller.abort();
        }

        this.dataset.loading = 'false';

        if (this.input) {
          this.input.value = '';
          this.input.focus();
        }

        if (this.link) {
          this.link.href = `${window.Shopify.routes.root}search?q=`;
        }

        this.clearResults();
      }

      clearResults() {
        if (this.productsList) this.productsList.innerHTML = '';
        if (this.collectionsList) this.collectionsList.innerHTML = '';
      }

      renderRecentSearches() {
        const storage = localStorage.getItem('search-terms');
        const terms = storage !== null ? JSON.parse(storage) : [];

        if (terms.length > 0 && this.recent) {
          const list = this.recent;

          terms.forEach((/** @type {string} */ term) => {
            const li = document.createElement('li');

            li.classList.add('predictive-search-recent-list-item');

            li.innerHTML = `<a href="/search?q=${term}">${term}</a>`;

            list.append(li);
          });
        }
      }
    },
  );
}
